// Flows — the registry of panel-authored clicker flows. Its own top-level page
// (`#!/flows`, sidebar item), superadmin-only via can('page.flows').
//
// A flow is authored for one app and one task type, then assigned to teams (or marked
// global). It only affects a launch once it is assigned: an unassigned flow is a draft.
//
// This page is a LIST: create, open, clone, delete, with the same row shape and the same
// filter bar as the farm lists (app / task type / teams — see `flow-filter.ts`).
// Everything about one flow — its name, note, enabled/global switches and team assignment
// — is edited inside that flow's own page (`#!/flows/:id`), next to the steps it belongs
// to, instead of as controls crammed into the row.
import { esc } from '../ui/modal';
import { navigate } from '../core/router';
import { t } from '../ui/lang';
import { fetchPayoutFlows, fetchFlowScopes, createPayoutFlow, deletePayoutFlow, clonePayoutFlow, emptyFlowDoc, flowProblems, TASK_TYPES, } from './flows-shared';
import { fetchClickerApps, appSupportsTask, clickerAppLabel } from '../clicker/clicker-apps-shared';
import { pickFlowFile } from './flow-json';
import { teamBadge } from '../core/farm-row';
import { createFlowFilterState, buildFlowFilter, applyFlowFilter, stampFlowFilterData, } from './flow-filter';
// Row actions. Stroked 24-box icons like every other control in the panel. Opening has no
// icon: the ROW is the button (see the click handler below) — a pencil next to a row that
// already opens on click is a second way to do the same thing, and the narrower target.
const CLONE_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"/></svg>';
const TRASH_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"/><path d="M10 11v6M14 11v6"/></svg>';
// The filter selection is module-level so it survives a re-render (the same reason the
// farm lists keep theirs there): deleting a flow reloads the list, and a filter that
// reset itself would drop the operator back into the whole registry.
const flowsFilter = createFlowFilterState();
// Team pills for one flow, in the row's badge cluster like a farm's team badge. A global
// flow shows one "all teams" pill instead of the whole list of them.
function teamBadges(f, scopes) {
    if (f.is_global)
        return `<span class="team-badge">${esc(t('pf.all_teams'))}</span>`;
    return f.team_ids
        .map((id) => teamBadge(scopes.teams.find((tm) => tm.id === id)?.name ?? String(id)))
        .join('');
}
export async function renderFlows() {
    const content = document.getElementById('content');
    document.getElementById('page-title').textContent = t('pf.flows');
    content.innerHTML = '<div class="flows-page"></div>';
    const panel = content.querySelector('.flows-page');
    panel.innerHTML = `
        <div class="settings-section">
            <h3 class="settings-section__title">${esc(t('pf.new_flow'))}</h3>
            <form class="settings-app-form pf-add" novalidate>
                <div class="modal__field">
                    <label>${esc(t('pf.task_type'))}</label>
                    <select class="settings-input pf-task" title="${esc(t('pf.task_type_hint'))}">
                        ${TASK_TYPES.map((tt) => `<option value="${tt}">${esc(t(`pf.task_${tt}`))}</option>`).join('')}
                    </select>
                </div>
                <div class="modal__field">
                    <label>${esc(t('pf.app'))}</label>
                    <select class="settings-input pf-app" required title="${esc(t('pf.app_hint'))}"></select>
                </div>
                <div class="modal__field">
                    <label>${esc(t('pf.name'))}</label>
                    <input class="settings-input pf-name" type="text" autocomplete="off" maxlength="80" required />
                </div>
                <div class="pf-add__actions">
                    <button type="submit" class="modal__submit">${esc(t('pf.create'))}</button>
                    <button type="button" class="modal__cancel pf-import" title="${esc(t('pf.import_hint'))}">${esc(t('pf.import_json'))}</button>
                </div>
            </form>
            <div class="settings-error modal__error pf-add-error" style="display:none"></div>
        </div>
        <div class="settings-section">
            <h3 class="settings-section__title">${esc(t('pf.flows'))}</h3>
            <div class="farm-filter pf-filter"></div>
            <div class="pf-list clicker-farm-list">${esc(t('common.loading'))}</div>
            <p class="settings-hint pf-empty" style="display:none">${esc(t('pf.no_flows_match'))}</p>
        </div>
    `;
    const form = panel.querySelector('.pf-add');
    const taskSel = panel.querySelector('.pf-task');
    const appSel = panel.querySelector('.pf-app');
    const nameInput = panel.querySelector('.pf-name');
    const addError = panel.querySelector('.pf-add-error');
    const listEl = panel.querySelector('.pf-list');
    const filterEl = panel.querySelector('.pf-filter');
    const emptyEl = panel.querySelector('.pf-empty');
    const showAddErr = (m) => { addError.textContent = m; addError.style.display = 'block'; };
    let scopes = { teams: [] };
    let flows = [];
    let apps = [];
    // A flow is written for one (task type, app) pair, so the app list follows the type:
    // only apps the catalog says can run that task are offered.
    const repopulateApps = () => {
        const tt = taskSel.value;
        const usable = apps.filter((a) => appSupportsTask(a, tt));
        appSel.innerHTML = usable.length
            ? usable.map((a) => `<option value="${esc(a.name)}">${esc(clickerAppLabel(a))}</option>`).join('')
            : `<option value="">${esc(t('pf.no_apps_for_task'))}</option>`;
    };
    taskSel.addEventListener('change', repopulateApps);
    async function reload() {
        try {
            [flows, scopes, apps] = await Promise.all([
                fetchPayoutFlows(), fetchFlowScopes(), fetchClickerApps().catch(() => []),
            ]);
            repopulateApps();
            renderList();
        }
        catch (e) {
            listEl.innerHTML = `<p class="settings-error">${esc(e.message)}</p>`;
        }
    }
    /** Re-apply the filter and say when it has hidden everything. */
    function applyFilter() {
        const shown = applyFlowFilter(listEl, flowsFilter);
        emptyEl.style.display = shown ? 'none' : 'block';
    }
    function renderList() {
        emptyEl.style.display = 'none';
        if (!flows.length) {
            filterEl.innerHTML = '';
            listEl.innerHTML = `<p class="settings-hint">${esc(t('pf.no_flows'))}</p>`;
            return;
        }
        listEl.innerHTML = flows.map((f) => {
            const problems = flowProblems(f.flow, f.task_type);
            const steps = (f.flow?.steps ?? []).length;
            // A flow reaches no launch until it is enabled and either global or assigned to a
            // team. The dot says all of that at a glance, the way a farm row's dot says online,
            // so the meta line carries no state sentence of its own.
            const assigned = f.enabled && (f.is_global || f.team_ids.length > 0);
            const meta = [f.note, t(steps === 1 ? 'pf.n_steps_one' : 'pf.n_steps', { n: String(steps) })].filter(Boolean).join(' · ');
            // ONE line per flow, shaped like a farm row (dot, name, badges, meta, chevron):
            // both pages are registries read by scanning them, so they read the same way.
            return `
            <div class="clicker-farm-row pf-row" data-id="${f.id}" role="button" tabindex="0" title="${esc(t('pf.open_flow'))}">
                <span class="status-dot ${assigned ? 'status-dot--green' : 'status-dot--red'}" title="${esc(assigned ? t('pf.live') : t('pf.draft'))}"></span>
                <span class="clicker-farm-row__name">${esc(f.name)}</span>
                <span class="farm-type-badge">${esc(t(`pf.task_${f.task_type}`))}</span>
                ${f.default_pick ? `<span class="farm-type-badge">${esc(t('pf.default_pick'))}</span>` : ''}
                <span class="pf-badge pf-badge--app">${esc(f.app)}</span>
                ${teamBadges(f, scopes)}
                ${problems.length ? `<span class="pf-badge pf-badge--warn" title="${esc(problems.join('; '))}">${esc(t(problems.length === 1 ? 'pf.n_problems_one' : 'pf.n_problems', { n: String(problems.length) }))}</span>` : ''}
                <span class="clicker-farm-row__meta">${esc(meta)}</span>
                <span class="pf-row__actions">
                    <button type="button" class="pf-ico pf-clone" title="${esc(t('pf.clone'))}" aria-label="${esc(t('pf.clone'))}">${CLONE_SVG}</button>
                    <button type="button" class="pf-ico pf-ico--del pf-del" title="${esc(t('common.delete'))}" aria-label="${esc(t('common.delete'))}">${TRASH_SVG}</button>
                </span>
                <span class="clicker-farm-row__chevron">›</span>
            </div>`;
        }).join('');
        listEl.querySelectorAll('.pf-row').forEach((row) => {
            const id = Number(row.dataset['id']);
            const rec = flows.find((x) => x.id === id);
            if (rec)
                stampFlowFilterData(row, rec);
            // The whole row opens the flow. The two icon buttons sit inside it and do
            // something else entirely, so they stop the click before it gets here.
            const open = () => { navigate({ page: 'flows', flowId: id }); };
            row.addEventListener('click', open);
            row.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ')
                    return;
                e.preventDefault(); // Space would scroll the page instead
                open();
            });
            // A clone opens straight away: you cloned it to change something in it.
            row.querySelector('.pf-clone').addEventListener('click', async (e) => {
                e.stopPropagation();
                try {
                    const copy = await clonePayoutFlow(id);
                    navigate({ page: 'flows', flowId: copy.id });
                }
                catch (err) {
                    alert(err.message);
                }
            });
            const delBtn = row.querySelector('.pf-del');
            delBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                // A flow is a document someone wrote locator by locator, and there is no
                // undo: ask by name, in words, rather than arming an icon whose second
                // click is indistinguishable from a double-click on the first.
                const f = flows.find((x) => x.id === id);
                const live = f?.enabled && (f.is_global || f.team_ids.length > 0);
                const question = `${t('pf.confirm_delete_flow', { name: f?.name ?? String(id) })}`
                    // Deleting an assigned flow is not just losing the document: every
                    // device pointed at it (payout_flow_id, ON DELETE SET NULL) silently
                    // drops back to whatever its team gets, or to the built-in Python flow.
                    + (live ? `\n\n${t('pf.confirm_delete_flow_live')}` : '');
                if (!confirm(question))
                    return;
                try {
                    await deletePayoutFlow(id);
                    await reload();
                }
                catch (e) {
                    alert(e.message);
                }
            });
        });
        // The bar is rebuilt from the flows just rendered (its app chips are theirs), and
        // the selection carried over from the last render is re-applied to the new rows.
        buildFlowFilter(filterEl, flowsFilter, flows, scopes.teams, applyFilter);
        applyFilter();
    }
    // Import: a file becomes a NEW flow, always an unassigned draft — an import can no more
    // change what a running device does than creating one by hand can. The name is taken
    // from the file and made unique, so importing the same file twice is two drafts rather
    // than a 409 the operator has to decode.
    panel.querySelector('.pf-import').addEventListener('click', async () => {
        addError.style.display = 'none';
        try {
            const file = await pickFlowFile();
            if (!file)
                return;
            const taken = new Set(flows.map((f) => f.name));
            let name = file.name ?? `${file.app} flow`;
            if (taken.has(name)) {
                const base = name;
                for (let i = 2; taken.has(name); i += 1)
                    name = `${base} (${i})`;
            }
            const created = await createPayoutFlow({
                task_type: file.task_type ?? taskSel.value,
                app: file.app,
                name,
                ...(file.note ? { note: file.note } : {}),
                flow: file.flow,
            });
            navigate({ page: 'flows', flowId: created.id });
        }
        catch (err) {
            showAddErr(err.message);
        }
    });
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        addError.style.display = 'none';
        const app = appSel.value.trim();
        const name = nameInput.value.trim();
        if (!app) {
            showAddErr(t('pf.err_app_required'));
            return;
        }
        if (!name) {
            showAddErr(t('pf.err_name_required'));
            return;
        }
        try {
            const created = await createPayoutFlow({
                task_type: taskSel.value,
                app, name, flow: emptyFlowDoc(app),
            });
            nameInput.value = '';
            navigate({ page: 'flows', flowId: created.id });
        }
        catch (err) {
            showAddErr(err.message);
        }
    });
    await reload();
}
